import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { eventsForRow, isFromDal } from "@/lib/scheduleTime";
import { logger } from "@/lib/logger";

// ------------------ Sound synthesis ------------------
const getAudioContext = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    return new AudioCtx();
  } catch (err) {
    logger.debug("WebAudio unavailable", err);
    return null;
  }
};

const scheduleTone = (ctx, start, freq, dur, gainLevel = 0.35, type = "square") => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
  gain.gain.exponentialRampToValueAtTime(
    gainLevel,
    ctx.currentTime + start + 0.015,
  );
  gain.gain.exponentialRampToValueAtTime(
    0.0001,
    ctx.currentTime + start + dur,
  );
  osc.connect(gain).connect(ctx.destination);
  osc.start(ctx.currentTime + start);
  osc.stop(ctx.currentTime + start + dur + 0.02);
};

// STARTUP (T-30) — gentle 3-note ascending chime, informational
const playStartupSound = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  scheduleTone(ctx, 0.0, 784, 0.22, 0.28, "triangle"); // G5
  scheduleTone(ctx, 0.28, 988, 0.22, 0.28, "triangle"); // B5
  scheduleTone(ctx, 0.56, 1319, 0.4, 0.3, "triangle"); // E6
  setTimeout(() => ctx.close(), 1400);
};

// DEPARTURE — urgent siren, alternating high/low tones, louder & sharper
const playDepartureSound = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  // 6 alternating hi/lo blasts (~0.9s) then long final blast
  for (let i = 0; i < 6; i++) {
    const freq = i % 2 === 0 ? 1760 : 1109; // A6 / C#6
    scheduleTone(ctx, i * 0.14, freq, 0.13, 0.55, "sawtooth");
  }
  // Final sustained warning tone
  scheduleTone(ctx, 0.9, 1568, 0.55, 0.6, "sawtooth");
  scheduleTone(ctx, 0.9, 784, 0.55, 0.35, "square");
  setTimeout(() => ctx.close(), 1800);
};

const playAlarmSound = (kind) => {
  if (kind === "departure") playDepartureSound();
  else playStartupSound();
};

// ------------------ Browser notification ------------------
const showBrowserNotification = (event) => {
  try {
    const title =
      event.kind === "startup"
        ? `T-30 STARTUP — TS ${event.row.trainset_id}`
        : `DEPARTURE NOW — TS ${event.row.trainset_id}`;
    const body =
      `${event.row.track_origin} → ${event.row.track_destination}` +
      `  ·  ${event.row.start_time}  ·  ${event.row.operation_id}`;
    const n = new Notification(title, {
      body,
      tag: event.id,
      requireInteraction: true,
    });
    n.onclick = () => window.focus();
  } catch (err) {
    logger.debug("Notification failed", err);
  }
};

// ------------------ Event pipeline ------------------
const buildEvents = (rows, refDate, planDate) => {
  const evts = [];
  rows.forEach((r) => {
    const { departureAt, startupAt } = eventsForRow(r, refDate, planDate);
    // Baris keberangkatan dari DAL depo: dispatcher tidak perlu diingatkan
    // untuk startup T-30 (masinis sudah standby di depo). Hanya event
    // departure yang di-schedule.
    if (!isFromDal(r)) {
      evts.push({ id: `${r.id}-startup`, kind: "startup", row: r, at: startupAt });
    }
    evts.push({ id: `${r.id}-departure`, kind: "departure", row: r, at: departureAt });
  });
  evts.sort((a, b) => a.at - b.at);
  return evts;
};

// ------------------ Persistence helpers ------------------
// Dismissed / already-fired alarm IDs are persisted per-day in localStorage
// so that a page reload (or accidental refresh) does NOT re-fire alarms the
// dispatcher has already acknowledged for that day.
const STORAGE_PREFIX = "mrt_fired_alarms_";

const loadFiredSet = (dayKey) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${dayKey}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
};

const saveFiredSet = (dayKey, set) => {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${dayKey}`,
      JSON.stringify(Array.from(set)),
    );
  } catch (err) {
    // Quota exceeded / private mode / storage disabled — degrade gracefully.
    // Dismiss persistence is a nice-to-have; the in-memory Set still works
    // for the current session so we don't surface an error to the user.
    console.warn("[useAlarmEngine] saveFiredSet skipped:", err?.message || err);
  }
};

// Purge old day buckets (keep only current day) to avoid unbounded growth.
const purgeOldDayKeys = (currentDayKey) => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (
        k &&
        k.startsWith(STORAGE_PREFIX) &&
        k !== `${STORAGE_PREFIX}${currentDayKey}`
      ) {
        localStorage.removeItem(k);
      }
    }
  } catch (err) {
    // Storage may be inaccessible (private mode, quota) — old keys will be
    // overwritten anyway on next successful write, so it's safe to skip.
    console.warn("[useAlarmEngine] purgeOldDayKeys skipped:", err?.message || err);
  }
};

/**
 * useAlarmEngine — fires distinct sounds for startup vs departure.
 * Startup → gentle rising chime. Departure → urgent siren.
 *
 * Dismissed / already-fired alarms are persisted to localStorage per-day so
 * they never re-trigger after a page reload within the same operational day.
 */
export function useAlarmEngine({ rows, now, soundOn, notifPerm, planDate }) {
  const dayKey = useMemo(
    () => `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`,
    [now],
  );

  // Lazy-init from storage so first render already knows which alarms are
  // dismissed. Ref is mutated in-place; `firedVersion` state bumps to force
  // re-renders when we need consumers to react (rare — mainly for tests).
  const firedRef = useRef(null);
  if (firedRef.current === null) {
    firedRef.current = loadFiredSet(dayKey);
    purgeOldDayKeys(dayKey);
  }

  const audioLoopRef = useRef(null);
  const [activeAlarm, setActiveAlarm] = useState(null);
  const currentDayKeyRef = useRef(dayKey);
  // Ref shadow of activeAlarm supaya fire-effect closure tidak stale
  // saat beberapa alarm bertumpuk dalam satu tick.
  const activeAlarmRef = useRef(null);
  useEffect(() => {
    activeAlarmRef.current = activeAlarm;
  }, [activeAlarm]);
  // Queue alarm yang belum sempat ditampilkan karena masih ada aktif.
  const pendingAlarmsRef = useRef([]);

  // `buildEvents` is a pure module-scope helper; depending on `rows` + `now`
  // + `planDate` is sufficient and satisfies react-hooks/exhaustive-deps.
  const events = useMemo(() => buildEvents(rows, now, planDate), [rows, now, planDate]);

  const upcoming = useMemo(() => {
    for (const e of events) {
      const diff = (e.at - now) / 1000;
      if (diff > -1) return { ...e, secondsRemaining: diff };
    }
    return null;
  }, [events, now]);

  const triggerAlarm = useCallback(
    (event) => {
      // WAJIB clear interval audio lama sebelum start yang baru. Kalau tidak,
      // beberapa interval bisa hidup bersamaan (bug: alarm tidak mau berhenti
      // setelah di-acknowledge kalau ada alarm bertumpuk).
      if (audioLoopRef.current) {
        clearInterval(audioLoopRef.current);
        audioLoopRef.current = null;
      }
      setActiveAlarm(event);
      activeAlarmRef.current = event;
      if (soundOn) {
        playAlarmSound(event.kind);
        // Departure loops faster (2.5s) than startup (5s) — reinforces urgency.
        const interval = event.kind === "departure" ? 2500 : 5000;
        audioLoopRef.current = setInterval(() => playAlarmSound(event.kind), interval);
      }
      if (notifPerm === "granted" && "Notification" in window) {
        showBrowserNotification(event);
      }
    },
    [soundOn, notifPerm],
  );

  useEffect(() => {
    for (const e of events) {
      const diffSec = (now - e.at) / 1000;
      if (diffSec >= 0 && diffSec < 30 && !firedRef.current.has(e.id)) {
        firedRef.current.add(e.id);
        saveFiredSet(dayKey, firedRef.current);
        // Kalau sudah ada alarm aktif yang belum di-ack, queue-kan yang baru
        // supaya dispatcher menerima satu per satu (tidak silent-overwrite).
        if (activeAlarmRef.current) {
          if (!pendingAlarmsRef.current.some((p) => p.id === e.id)) {
            pendingAlarmsRef.current.push(e);
          }
        } else {
          triggerAlarm(e);
        }
      }
    }
  }, [now, events, triggerAlarm, dayKey]);

  const dismissAlarm = useCallback(() => {
    const dismissedId = activeAlarmRef.current?.id;
    if (dismissedId) {
      firedRef.current.add(dismissedId);
      saveFiredSet(currentDayKeyRef.current, firedRef.current);
    }
    // Selalu clear interval audio sebelum keputusan next state.
    if (audioLoopRef.current) {
      clearInterval(audioLoopRef.current);
      audioLoopRef.current = null;
    }
    // Ambil alarm berikutnya dari antrian (kalau ada). Skip yang sudah "kadaluarsa"
    // (lewat 30s dari waktu event) supaya tidak muncul alarm zombie.
    const nowMs = Date.now();
    let next = null;
    while (pendingAlarmsRef.current.length > 0) {
      const candidate = pendingAlarmsRef.current.shift();
      const age = (nowMs - candidate.at.getTime()) / 1000;
      if (age >= 0 && age < 60) {
        next = candidate;
        break;
      }
      // Terlalu lama — anggap sudah tersimpan sebagai "fired" saja.
    }
    if (next) {
      triggerAlarm(next);
    } else {
      setActiveAlarm(null);
      activeAlarmRef.current = null;
    }
  }, [triggerAlarm]);

  useEffect(() => {
    const ref = audioLoopRef;
    return () => {
      if (ref.current) clearInterval(ref.current);
    };
  }, []);

  // Day rollover: reset in-memory state and load fresh set for the new day.
  useEffect(() => {
    if (currentDayKeyRef.current !== dayKey) {
      currentDayKeyRef.current = dayKey;
      firedRef.current = loadFiredSet(dayKey);
      purgeOldDayKeys(dayKey);
      setActiveAlarm(null);
      activeAlarmRef.current = null;
      pendingAlarmsRef.current = [];
      if (audioLoopRef.current) {
        clearInterval(audioLoopRef.current);
        audioLoopRef.current = null;
      }
    }
  }, [dayKey]);

  return { activeAlarm, dismissAlarm, upcoming };
}
